import { Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { TrustScoreApi } from "../../apis";
import { DotPill } from "../delivery/atoms";
import { TrustLevelBadge } from "./trust-level-badge";

interface Props {
  businessId: string;
  size?: "xs" | "sm" | "md" | "lg";
  inline?: boolean;
}

export function TrustBadge ({ businessId, size = "sm", inline = false }: Props){
  const { t } = useTranslation("returns");
  const { data, isLoading, error } = useSWR(
    businessId ? ["trust-score.get", businessId] : null,
    () => TrustScoreApi.get(businessId),
    { revalidateOnFocus: false }
  );

  if (isLoading) {
    return <Skeleton height={inline ? 18 : 24} width={90} />;
  }

  if (error || !data) {
    // No score yet (cron hasn't computed) — neutral placeholder.
    return <DotPill tone="grey">{t("trustBadge.notRated")}</DotPill>;
  }

  const tooltip = (
    <Stack gap={2}>
      <Text size="sm">{t("trustBadge.score", { score: data.rawScore.toFixed(1) })}</Text>
      <Text size="sm">{t("trustBadge.returns", { returns: data.totalReturns, orders: data.totalOrders })}</Text>
      <Text size="sm">{t("trustBadge.disputes", { count: data.disputeCount })}</Text>
    </Stack>
  );

  return (
    <Tooltip label={tooltip} withArrow>
      <TrustLevelBadge level={data.level} size={size} />
    </Tooltip>
  );
}

/** One line under the trust pill: «6/40 буцаалт · 2 маргаан» (canvas Ord_ReturnDetail). */
export function TrustSummaryLine ({ businessId }: { businessId: string }){
  const { t } = useTranslation("returns");
  const { data } = useSWR(
    businessId ? ["trust-score.get", businessId] : null,
    () => TrustScoreApi.get(businessId),
    { revalidateOnFocus: false }
  );
  if (!data) return null;
  return (
    <Text size="sm" c="var(--nw-text-2)">
      {t("trustBadge.summary", { returns: data.totalReturns, orders: data.totalOrders, count: data.disputeCount })}
    </Text>
  );
}
